import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert } from './ui/alert';
import { Label } from './ui/label';
import { useNavigate } from 'react-router-dom';
import {
  Bell, AlertTriangle, CheckCircle, XCircle, Camera, Shield, Flame,
  UserX, Car, Eye, Settings, Clock, Filter, Search, MoreVertical,
  Check, X, Phone, User, MapPin, Building, Home, Activity, Zap,
  Maximize2, Download, RefreshCw, ChevronLeft, ChevronRight, Hash,
  Target, ShieldAlert, Cpu
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// --- Shared Glass Components ---

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any; headerAction?: React.ReactNode }> = ({ children, className = '', title, icon: Icon, headerAction }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 shadow-2xl ${className}`}>
    {(title || Icon || headerAction) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase pr-4">{title}</h3>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="p-6">
      {children}
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: string; icon: any; color: string; bgColor: string; sub?: string }> = ({ label, value, icon: Icon, color, bgColor, sub }) => (
  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
    <div className={`absolute top-0 right-0 w-24 h-24 ${bgColor} blur-[40px] opacity-20 -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700`} />
    <div className="flex items-center gap-4 relative z-10">
      <div className={`w-12 h-12 rounded-xl ${bgColor} border border-white/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">{label}</p>
        <h4 className="text-2xl font-black text-white">{value}</h4>
        {sub && <p className="text-[9px] text-blue-100/20 mt-1 uppercase font-bold">{sub}</p>}
      </div>
    </div>
  </div>
);

// --- Alert Sub-Component ---

interface SystemAlert {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  source: string;
  priority: string;
  read: boolean;
  image_url?: string;
  metadata?: any;
  userDetails?: any;
}

const AlertCard: React.FC<{ alert: SystemAlert; isSelected: boolean; onClick: () => void }> = ({ alert, isSelected, onClick }) => {
  const getSeverityStyles = (priority: string) => {
    switch (priority) {
      case 'critical': return { glow: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', bar: 'bg-red-500' };
      case 'high': return { glow: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', bar: 'bg-orange-500' };
      case 'medium': return { glow: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', bar: 'bg-blue-500' };
      default: return { glow: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', bar: 'bg-emerald-500' };
    }
  };

  const styles = getSeverityStyles(alert.priority);

  return (
    <div 
      onClick={onClick}
      className={`relative group bg-white/5 border ${isSelected ? 'border-blue-500/50' : 'border-white/10'} rounded-2xl overflow-hidden cursor-pointer transition-all hover:bg-white/[0.08]`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${styles.bar}`} />
      
      <div className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Thumbnail */}
          {alert.image_url && (
            <div className="w-full lg:w-48 aspect-video lg:aspect-auto h-32 rounded-xl overflow-hidden border border-white/10 relative">
               <img src={alert.image_url} alt="Detection" className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 transition-all duration-500" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
               <div className="absolute bottom-2 right-2 p-1.5 bg-black/50 backdrop-blur-md rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="w-3.5 h-3.5 text-white" />
               </div>
            </div>
          )}

          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
               <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${styles.glow} ${styles.text} border ${styles.border}`}>
                    {alert.priority}
                  </span>
                  {!alert.read && <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shadow-[0_0_10px_#3b82f6]" />}
               </div>
               <span className="text-[10px] font-mono text-blue-100/20 uppercase">
                  {new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
               </span>
            </div>

            <div>
              <h3 className="text-sm font-black text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">{alert.title}</h3>
              <p className="text-xs text-blue-100/60 mt-1 font-light leading-relaxed">{alert.message}</p>
            </div>

            <div className="flex items-center gap-4 border-t border-white/5 pt-3">
               <div className="flex items-center gap-1.5">
                  <Target className="w-3 h-3 text-blue-500/40" />
                  <span className="text-[10px] font-bold text-blue-100/40 uppercase">{alert.metadata?.camera_name || 'Node Unknown'}</span>
               </div>
               <div className="flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-blue-500/40" />
                  <span className="text-[10px] font-bold text-blue-100/40 uppercase">{alert.metadata?.location || 'Sector Zero'}</span>
               </div>
            </div>
          </div>
        </div>

        {/* Expanded View */}
        <AnimatePresence>
          {isSelected && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-6 border-t border-white/10 pt-6 overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Neural Metadata</h4>
                    <div className="grid grid-cols-2 gap-4">
                       {[
                         { l: 'Confidence', v: `${((alert.metadata?.confidence || 0.94) * 100).toFixed(1)}%` },
                         { l: 'Usecase', v: alert.metadata?.usecase || 'General' },
                         { l: 'Logic Engine', v: alert.source },
                         { l: 'Alert ID', v: alert.id.slice(0, 12) }
                       ].map((item, idx) => (
                         <div key={idx}>
                            <p className="text-[9px] font-bold text-blue-100/20 uppercase">{item.l}</p>
                            <p className="text-xs font-mono text-blue-100/80">{item.v}</p>
                         </div>
                       ))}
                    </div>
                 </div>

                 {alert.userDetails && (
                   <div className="space-y-4">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Identity Reconciliation</h4>
                      <div className="bg-white/[0.03] rounded-xl p-4 border border-white/5 space-y-2">
                         <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-blue-500" />
                            <span className="text-xs font-bold text-white uppercase">{alert.userDetails.name}</span>
                         </div>
                         <div className="flex items-center gap-2 ml-5">
                            <Hash className="w-3 h-3 text-blue-100/20" />
                            <span className="text-[10px] text-blue-100/40">{alert.userDetails.email}</span>
                         </div>
                      </div>
                   </div>
                 )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// --- Main Alerts Component ---

export function Alerts({ systemAlerts = [], onUpdateAlerts, onAddAlert, initialFilter = 'all' }: any) {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);
  const [realTimeAlerts, setRealTimeAlerts] = useState<SystemAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  
  // Advanced filters
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedUsecase, setSelectedUsecase] = useState('all');
  const [selectedCamera, setSelectedCamera] = useState('all');

  const fetchAlerts = useCallback(async (filters: any = {}) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('csense_auth_token');
      const params = new URLSearchParams({ limit: '100', page: '1' });
      if (filters.usecase && filters.usecase !== 'all') params.append('usecase', filters.usecase);
      if (filters.priority && filters.priority !== 'all') params.append('priority', filters.priority);

      const response = await fetch(`/api/alerts?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const formatted = data.alerts.map((a: any) => ({
           id: a.id || a._id,
           title: `${(a.type || 'detection').replace(/_/g, ' ').toUpperCase()} - ${a.camera_name || 'Camera'}`,
           message: a.message || 'Detection logged',
           timestamp: a.timestamp,
           source: a.usecase || 'AI Engine',
           priority: a.priority || 'medium',
           read: a.status === 'read',
           image_url: a.image_url,
           metadata: {
             camera_name: a.camera_name,
             location: a.location,
             confidence: a.confidence,
             usecase: a.usecase
           },
           userDetails: a.userDetails
        }));
        setRealTimeAlerts(formatted);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAlerts({ usecase: selectedUsecase, priority: priorityFilter });
    const interval = setInterval(() => fetchAlerts({ usecase: selectedUsecase, priority: priorityFilter }), 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts, selectedUsecase, priorityFilter]);

  // Integration for real-time popups
  useEffect(() => {
    import('../services/AlertService').then(({ alertService }) => {
      alertService.setOnAlertsUpdate((newAlerts) => {
        const formatted = newAlerts.map(a => ({
          id: `new-${Date.now()}-${Math.random()}`,
          title: `DETECTED: ${a.type?.toUpperCase()}`,
          message: a.message,
          type: a.priority === 'critical' ? 'critical' : 'warning',
          timestamp: new Date()
        }));
        setNotifications(prev => [...formatted, ...prev].slice(0, 5));
        setTimeout(() => setNotifications(prev => prev.slice(0, -1)), 15000);
      });
    });
  }, []);

  const unreadCount = realTimeAlerts.filter(a => !a.read).length;
  const criticalCount = realTimeAlerts.filter(a => a.priority === 'critical' || a.priority === 'high').length;

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      {/* Background Atmosphere */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-500/5 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 blur-[100px]" />
      </div>

      <div className="relative z-10 space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black tracking-tighter uppercase flex items-center gap-4">
               Intelligence Log
               <div className="flex items-center gap-2">
                 <div className="w-2 h-pill px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]" />
                    <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Live Sync</span>
                 </div>
               </div>
            </h1>
            <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Tactical monitoring and neural event history across all authorized sectors.</p>
          </div>

          <div className="flex items-center gap-3">
             <Button variant="outline" className="border-white/10 text-white/60 hover:bg-white/5 rounded-2xl px-6 py-6" onClick={() => fetchAlerts()}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Sync Log
             </Button>
             <Button className="bg-blue-600 hover:bg-blue-500 text-white font-black px-8 py-6 rounded-2xl shadow-[0_0_30px_rgba(37,99,235,0.3)]">
                <Download className="w-4 h-4 mr-2" /> Export Intel
             </Button>
          </div>
        </div>

        {/* Real-time Triage Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard label="Critical Alerts" value={criticalCount.toString()} icon={ShieldAlert} color="text-red-400" bgColor="bg-red-500/10" sub="Immediate response needed" />
          <StatCard label="Pending Triage" value={unreadCount.toString()} icon={Bell} color="text-orange-400" bgColor="bg-orange-500/10" sub="New events detected" />
          <StatCard label="Engines Active" value="12" icon={Cpu} color="text-blue-400" bgColor="bg-blue-500/10" sub="Neural integrity optimal" />
          <StatCard label="Global Log" value={realTimeAlerts.length.toString()} icon={Activity} color="text-emerald-400" bgColor="bg-emerald-500/10" sub="Aggregate history" />
        </div>

        {/* Advanced Tactical Filtering */}
        <GlassPanel>
           <div className="flex flex-col lg:flex-row gap-6">
              <div className="relative flex-1">
                 <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-100/20" />
                 <Input 
                   value={searchTerm}
                   onChange={e => setSearchTerm(e.target.value)}
                   placeholder="Search intelligence cache..." 
                   className="bg-white/5 border-white/10 rounded-2xl py-6 pl-12 pr-6 text-white placeholder:text-blue-100/10 focus:ring-blue-500/20" 
                 />
              </div>
              <div className="flex flex-wrap gap-4">
                 <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                   <SelectTrigger className="w-48 bg-white/5 border-white/10 rounded-2xl h-full py-3 text-white">
                      <SelectValue placeholder="Priority Level" />
                   </SelectTrigger>
                   <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="critical">Critical Only</SelectItem>
                      <SelectItem value="high">High & Above</SelectItem>
                      <SelectItem value="medium">Medium & Above</SelectItem>
                   </SelectContent>
                 </Select>

                 <Select value={selectedUsecase} onValueChange={setSelectedUsecase}>
                   <SelectTrigger className="w-56 bg-white/5 border-white/10 rounded-2xl h-full py-3 text-white">
                      <SelectValue placeholder="Neural Engine" />
                   </SelectTrigger>
                   <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                      <SelectItem value="all">All Engines</SelectItem>
                      <SelectItem value="fire-detection">Fire & Smoke</SelectItem>
                      <SelectItem value="fall-detection">Patient Fall</SelectItem>
                      <SelectItem value="anpr">License Plate</SelectItem>
                   </SelectContent>
                 </Select>

                 <Button variant="ghost" className="text-blue-400 hover:bg-blue-400/10 rounded-xl px-4 py-3 h-full" onClick={() => { setPriorityFilter('all'); setSelectedUsecase('all'); setSearchTerm(''); }}>
                    <X className="w-4 h-4 mr-2" /> Reset
                 </Button>
              </div>
           </div>
        </GlassPanel>

        {/* Intelligence Timeline */}
        <div className="space-y-4">
           {realTimeAlerts.length > 0 ? (
             realTimeAlerts.map((alert, i) => (
                <AlertCard 
                  key={alert.id} 
                  alert={alert} 
                  isSelected={selectedAlert === alert.id} 
                  onClick={() => setSelectedAlert(selectedAlert === alert.id ? null : alert.id)} 
                />
             ))
           ) : (
             <div className="py-40 text-center border-2 border-dashed border-white/5 rounded-[40px] bg-white/[0.01]">
                {loading ? <RefreshCw className="w-12 h-12 text-blue-500/20 animate-spin mx-auto mb-6" /> : <Shield className="w-16 h-16 text-blue-500/10 mx-auto mb-6" />}
                <p className="text-blue-100/20 text-sm font-light italic uppercase tracking-widest">{loading ? 'Synchronizing Intelligence Database...' : 'Intelligence log is currently empty.'}</p>
             </div>
           )}
        </div>

        {/* Tactical Pagination */}
        <div className="flex items-center justify-between px-2 pt-8">
           <div className="text-[10px] font-black text-blue-100/20 uppercase tracking-widest">Log Page 1 // Matrix Synchronized</div>
           <div className="flex gap-2">
              <Button variant="outline" className="border-white/5 bg-white/5 text-blue-100/40 rounded-xl hover:text-white" disabled><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" className="border-white/5 bg-white/5 text-blue-100/40 rounded-xl hover:text-white" disabled><ChevronRight className="w-4 h-4" /></Button>
           </div>
        </div>
      </div>

      {/* Real-time Triage Notifications Container */}
      <div className="fixed top-6 right-6 z-[100] space-y-4 w-full max-w-sm">
        <AnimatePresence>
          {notifications.map((n, i) => (
            <motion.div 
              key={n.id}
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.9 }}
              className={`bg-[#0f111a] border-l-4 ${n.type === 'critical' ? 'border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.2)]' : 'border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.2)]'} border-white/10 rounded-2xl p-5 shadow-2xl backdrop-blur-3xl overflow-hidden relative`}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500 blur-[50px] opacity-[0.03]" />
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border border-white/10 ${n.type === 'critical' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
                   {n.type === 'critical' ? <ShieldAlert className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                   <h4 className="text-xs font-black text-white uppercase tracking-tight mb-1">{n.title}</h4>
                   <p className="text-[10px] text-blue-100/40 font-medium leading-relaxed">{n.message}</p>
                   <div className="mt-3 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-blue-100/20">{new Date(n.timestamp).toLocaleTimeString()}</span>
                      <button onClick={() => navigate('/alerts')} className="text-[9px] font-black text-blue-400 uppercase tracking-widest hover:underline">Intercept</button>
                   </div>
                </div>
                <button onClick={() => setNotifications(prev => prev.filter(item => item.id !== n.id))} className="text-blue-100/20 hover:text-white transition-colors">
                   <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}